import { TextFieldProps } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form' //@ts-ignore
import ReactInputMask from 'react-input-mask'
import InputField from '../../../../../components/customElements/InputField/InputField'

type FormInputProps<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
  label?: string
  placeholder: string
  handleChangeCallback?: (value: FieldValues) => FieldValues
  handleEditValue?: (value: FieldValues) => T
  fullWidth: boolean
  rows?: number
  multiline?: boolean
  defaultError?: boolean
  mask?: string
  capitalizeFirstLetter?: boolean
  onlyNumbers?: boolean
  onlyNumbersAndLetters?: boolean
  maxLength?: number
}

const FormInput = <T extends FieldValues>(
  props: FormInputProps<T> & TextFieldProps,
) => {
  const {
    control,
    fullWidth,
    label,
    name,
    defaultError,
    mask,
    capitalizeFirstLetter,
    onlyNumbers,
    onlyNumbersAndLetters,
    maxLength,
    ...otherProps
  } = props

  const handleOnChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    onChange: (...event: any[]) => void,
  ) => {
    if (capitalizeFirstLetter) {
      e.target.value =
        e?.target?.value?.charAt(0).toUpperCase() + e?.target?.value?.slice(1)

      onOnlyLettersAndSymbolsChange(e, onChange)
    } else if (onlyNumbers) {
      onOnlyNumbersChange(e, onChange)
    } else if (onlyNumbersAndLetters) {
      onOnlyLettersAndNumbersChange(e, onChange)
    } else {
      onChange(e?.target?.value)
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { value, onChange, ...field },
        fieldState: { error, isTouched, isDirty },
      }) => {
        const startError = !isTouched && !isDirty && defaultError

        if (mask) {
          return (
            <ReactInputMask
              mask={mask}
              onChange={onChange}
              maskPlaceholder={'/'}
              maskChar={null}
              value={value}
              formatChars={{
                9: '[0-9]',
                a: '[A-Za-z]',
                '*': '[A-Za-z0-9]',
              }}
              {...field}
            >
              {() => (
                <InputField
                  value={value}
                  fullWidth
                  territorySelect={false}
                  error={!!error?.message || startError}
                  label={label}
                  {...otherProps}
                />
              )}
            </ReactInputMask>
          )
        }

        return (
          <InputField
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleOnChange(e, onChange)
            }
            fullWidth
            territorySelect={false}
            error={!!error?.message || startError}
            label={label}
            capitalizeFirstLetter={capitalizeFirstLetter}
            inputProps={{ maxLength: maxLength }}
            {...field}
            {...otherProps}
          />
        )
      }}
    />
  )
}

const onOnlyNumbersChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (...event: any[]) => void,
) => {
  e.target.value = e?.target?.value.replace(/\D/g, '')

  onChange(e?.target?.value)
}

const onOnlyLettersAndNumbersChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (...event: any[]) => void,
) => {
  e.target.value = e?.target?.value.replace(/[^a-zA-Zа-яА-Я0-9]/g, '')

  onChange(e?.target?.value)
}

const onOnlyLettersAndSymbolsChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  onChange: (value: string) => void,
) => {
  // Разрешаем: буквы (лат + кир + ё), пробел, скобки, апостроф, дефис
  // Цифры исключены
  const cleanedValue = e.target.value.replace(/[^a-zA-Zа-яА-ЯёЁ \(\)'\-]/g, '')

  onChange(cleanedValue)
}

export default observer(FormInput)
